import { useEffect, useState, useRef } from 'react';
import vhStyle from './VisualHierarchy.style';
import {
	Box,
	Divider,
	Button,
	Typography,
	MenuItem,
	ListItemText,
	Accordion,
	AccordionDetails,
	Checkbox,
	AccordionSummary,
	InputBase,
	IconButton,
	Tooltip
} from '@material-ui/core';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import {
	CLEAR_ALL,
	CANCEL,
	OKAY,
	VISUAL_HIERARCHY_SEARCH_PLACEHOLDER,
	VISUAL_HIERARCHY_NO_RESULTS_TITLE,
	VISUAL_HIERARCHY_NO_RESULTS_DESCRIPTION
} from 'constants/constant';
import {
	DATA_LOAD_SIZE,
	BUTTON_LIST,
	VH_MAX_SELECT_MESSAGE,
	LEFT_PANEL_HEADER,
	MAX_OPTIONS_SELECT_LIMIT,
	SCROLL_EVENT,
	LEFT_PANEL_CONTAINER_ID
} from './VisualHierarchy.utils';
import { useSelector } from 'react-redux';

const VisualHierarchyWidget = (props) => {
	const { open, onClose, handleAddChip, filterName, filterList } = props;
	const list = useSelector((state) => state.previewReducer.visualHierarchyList);
	const [searchValue, setSearchValue] = useState('');
	const [checkboxData, setCheckboxData] = useState([]);
	const [filteredList, setFilteredList] = useState([]);
	const [optionsSelected, setOptionsSelected] = useState([]);
	const [optionsExpanded, setOptionsExpanded] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState('');
	const [menuOptionsCount, setMenuOptionsCount] = useState({});
	const [selectedDeptartment, setSelectedDeptartment] = useState('');
	const searchValueRef = useRef(null);

	const classes = vhStyle();
	const numberOfOptionsSelected = optionsSelected.length;
	const initialListToLoad = list.slice(0, DATA_LOAD_SIZE);

	useEffect(() => {
		setFilteredList(initialListToLoad);
		setMenuOptionsCount(initMenuOptions(list));
		setOptionsExpanded([initDeparmentName(list)]);
		setSelectedDeptartment(initDeparmentName(list));
		setOptionsSelected(initOptionsSelected(filterList));
		setCheckboxData(getDepartmentCheckboxData(list[0]));
	}, []);

	const handleScroll = (event) => {
		const { scrollTop, clientHeight, scrollHeight } = event.target;
		if (scrollHeight - scrollTop === clientHeight && (searchValueRef.current ?? '')?.length < 3) {
			setFilteredList((prevState) => [
				...prevState,
				...list.slice(prevState.length, prevState.length + DATA_LOAD_SIZE)
			]);
		}
	};

	useEffect(() => {
		if (filteredList.length > 0) {
			const listContainer = document.getElementById(LEFT_PANEL_CONTAINER_ID);
			listContainer?.addEventListener(SCROLL_EVENT, handleScroll);

			return () => listContainer?.removeEventListener(SCROLL_EVENT, handleScroll);
		}
	}, [filteredList]);

	function initMenuOptions(data) {
		const departmentCount = {};
		const selectedOptions = initOptionsSelected(filterList);
		data.forEach((department) => {
			const departmentName = department.values.name;
			let count = 0;
			department.values.subHierarchy.forEach((category) => {
				category.subHierarchy.forEach((item) => {
					if (selectedOptions.includes(item.id)) {
						count++;
					}
				});
			});
			departmentCount[departmentName] = count;
		});
		return departmentCount;
	}

	function initDeparmentName(data) {
		return data[0]?.values?.name;
	}

	function initOptionsSelected(filterList) {
		const filterItem = filterList.find((filter) => filter.item.businessName === filterName);
		return [...filterItem.item.filter.value];
	}

	function getDepartmentCheckboxData(data) {
		const updatedCheckboxData = [];
		if (!data) return updatedCheckboxData;
		const categoryArray = data.values.subHierarchy;
		categoryArray.forEach((category) => {
			category.subHierarchy.forEach((item) => {
				updatedCheckboxData.push(item);
			});
		});
		return updatedCheckboxData;
	}

	const isButtonDisabled = (selectedOptionsCount, buttonName) => {
		if (BUTTON_LIST.includes(buttonName)) return selectedOptionsCount === 0;
		return false;
	};

	const disableCheckboxOptions = (isChecked) => {
		return !isChecked ? numberOfOptionsSelected >= MAX_OPTIONS_SELECT_LIMIT : false;
	};

	const onOptionCheck = (value, deptName) => {
		if (optionsSelected.includes(value)) {
			setOptionsSelected((prevState) =>
				prevState.filter((currentSelection) => currentSelection !== value)
			);
			setMenuOptionsCount((prevState) => {
				return { ...prevState, [deptName]: prevState[deptName] - 1 };
			});
		} else {
			setOptionsSelected((prevState) => [...prevState, value]);
			setMenuOptionsCount((prevState) => {
				return { ...prevState, [deptName]: prevState[deptName] + 1 };
			});
		}
	};

	const handleAccordionExpansion = (itemName) => {
		if (optionsExpanded.includes(itemName)) {
			return setOptionsExpanded((prevState) => prevState.filter((item) => item !== itemName));
		}
		setOptionsExpanded((prevState) => [...prevState, itemName]);
	};

	function generateCheckboxOptions(data, isDepartment = true) {
		if (isDepartment) {
			const updatedCheckboxData = getDepartmentCheckboxData(data);
			return setCheckboxData(updatedCheckboxData);
		}
		setCheckboxData([...data.subHierarchy]);
	}

	const handleSearchInput = (inputValue) => {
		setSearchValue(inputValue);
		searchValueRef.current = inputValue;
		if (inputValue.length < 3) {
			setFilteredList(list);
			setFilteredList(initialListToLoad);
			const updatedCheckboxData = getDepartmentCheckboxData(list[0]);
			setSelectedDeptartment(initDeparmentName(list));
			return setCheckboxData(updatedCheckboxData);
		}

		const updatedFilteredList = list.flatMap((data) => {
			// If the department name matches with what the user has typed, then include the entire department in the filteredList
			if (data?.id?.toLowerCase().includes(inputValue?.toLowerCase())) {
				return [{ ...data }];
			}
			const department = data.values.subHierarchy.flatMap((dept) => {
				// If the category name matches with what the user has typed, then include the entire category in filteredList
				if (dept?.name?.toLowerCase().includes(inputValue?.toLowerCase())) {
					return [{ ...dept }];
				}
				const category = dept.subHierarchy.filter((catg) => {
					// Concatenate id and name (because that is what we render on the screen) and then check if the
					// typed in search string matches with the current subHierarchy item.
					const idName = `${catg.id} ${catg.name}`;
					return idName?.toLowerCase()?.includes(inputValue?.toLowerCase());
				});
				return category.length > 0 ? [{ ...dept, subHierarchy: category }] : [];
			});
			return department.length > 0
				? [{ ...data, values: { ...data.values, subHierarchy: department } }]
				: [];
		});

		setFilteredList(updatedFilteredList);
		generateCheckboxOptions(updatedFilteredList[0]);
		setSelectedCategory('');
		setSelectedDeptartment('');
	};

	const onOkay = () => {
		const item = filterList.find((filter) => filter.item.businessName === filterName);
		const filterIndex = item.index;
		handleAddChip(null, filterIndex, optionsSelected);
		onClose();
	};

	const handleClearSearch = () => {
		setSearchValue('');
		searchValueRef.current = null;
		setFilteredList([...initialListToLoad]);
		generateCheckboxOptions(list[0], true);
	};

	const shouldExpand = (itemName) => {
		return optionsExpanded.includes(itemName);
	};

	const onClearAll = () => {
		setOptionsSelected([]);
		setMenuOptionsCount(initMenuOptions(list));
	};

	const onCancel = () => {
		onClose();
	};

	if (!open) {
		return null;
	}

	const widgetLeftPanel = () => {
		return (
			<Box className={classes.vhWidgetLeftPanel}>
				{/* Left Panel Header */}
				<Box className={`${classes.panelHeaderBackground}`}>
					<Typography className={classes.vhWidgetHeaderContainer}>{LEFT_PANEL_HEADER}</Typography>
				</Box>

				{/* Left panel menu options */}

				<Box className={classes.leftPanelOptions} id={LEFT_PANEL_CONTAINER_ID}>
					<Box className={classes.accordionItemsContainer}>
						{filteredList.map((department, indx) => {
							if (!department) return null;
							const departmentName = department.values.name;
							const categoryArray = department.values.subHierarchy;
							const isDepartmentSelected =
								selectedDeptartment === departmentName && selectedCategory === '';
							return (
								<Accordion
									key={department.id}
									expanded={shouldExpand(departmentName)}
									onChange={() => handleAccordionExpansion(departmentName)}>
									<AccordionSummary>
										<MenuItem
											id={`${department.id}-${indx}`}
											className={
												isDepartmentSelected
													? `${classes.accordionSummaryItem} ${classes.menuItemLeftSelected}`
													: classes.accordionSummaryItem
											}
											onClick={() => {
												const isDepartment = true;
												setSelectedDeptartment(departmentName);
												setSelectedCategory('');
												generateCheckboxOptions(department, isDepartment);
											}}>
											<Box className={classes.accordionSummaryItemContainer}>
												{!shouldExpand(departmentName) && (
													<ArrowRightIcon className={classes.arrowStyles} />
												)}
												{shouldExpand(departmentName) && (
													<ArrowDropDownIcon className={classes.arrowStyles} />
												)}
												<Tooltip
													title={`${departmentName} ${
														menuOptionsCount[departmentName] > 0
															? menuOptionsCount[departmentName]
															: ''
													}`}
													placement="top"
													arrow>
													<ListItemText className={classes.accordionSummaryText}>
														{departmentName}{' '}
														{menuOptionsCount[departmentName] > 0
															? `(${menuOptionsCount[departmentName]})`
															: ''}
													</ListItemText>
												</Tooltip>
											</Box>
										</MenuItem>
									</AccordionSummary>
									<AccordionDetails>
										{categoryArray.map((category, i) => {
											const isCategorySelected =
												selectedCategory === category.name && selectedCategory !== '';
											return (
												<MenuItem
													id={`${category.id}-${i}`}
													key={category.id}
													className={
														isCategorySelected
															? `${classes.accordionDetailsItem} ${classes.menuItemLeftSelected}`
															: classes.accordionDetailsItem
													}
													onClick={() => {
														const isDepartment = false;
														setSelectedDeptartment(departmentName);
														setSelectedCategory(category.name);
														generateCheckboxOptions(category, isDepartment);
													}}>
													<Tooltip title={category.name} placement="top" arrow>
														<ListItemText
															className={classes.accordionDetailsText}
															primary={category.name}
														/>
													</Tooltip>
												</MenuItem>
											);
										})}
									</AccordionDetails>
								</Accordion>
							);
						})}
					</Box>
				</Box>
			</Box>
		);
	};

	const widgetRightPanel = () => {
		return (
			<Box className={classes.vhWidgetRightPanel}>
				{/* Right Panel Header */}
				<Box className={`${classes.panelHeaderBackground}`}>
					<Typography className={classes.vhWidgetHeaderContainer}>{`${filterName}`}</Typography>
				</Box>

				{/* Right panel menu options */}
				<Box className={classes.rightPanelOptions}>
					<Box id="vh-checkbox-container" className={classes.checkboxItemsContainer}>
						{checkboxData.map((item, indx) => {
							if (!item) return null;
							const isChecked = optionsSelected.includes(item.id);
							return (
								<MenuItem
									id={`${item.id}-${indx}`}
									className={classes.subMenuItem}
									key={item.id}
									disabled={disableCheckboxOptions(isChecked)}
									onClick={() => onOptionCheck(item.id, selectedDeptartment)}>
									<Checkbox
										size="medium"
										color="primary"
										checked={isChecked}
										className={classes.subMenuItemCheckBox}
									/>
									<Tooltip title={`${item.id}  ${item.name}`} placement="top" arrow>
										<ListItemText primary={`${item.id}  ${item.name}`} />
									</Tooltip>
								</MenuItem>
							);
						})}
					</Box>
				</Box>
			</Box>
		);
	};

	return (
		<Box className={classes.vhWidgetContainer}>
			<Box>
				<Box className={classes.search}>
					<Box className={classes.searchIcon}>
						<SearchIcon />
					</Box>
					<InputBase
						id="search-field"
						value={searchValue}
						placeholder={VISUAL_HIERARCHY_SEARCH_PLACEHOLDER}
						className={classes.searchInput}
						inputProps={{ 'aria-label': 'search' }}
						disabled={false}
						onChange={(e) => handleSearchInput(e.target.value)}
					/>
					{searchValue !== '' && (
						<Box className={classes.crossIcon}>
							<IconButton aria-label="Clear search" id="clear" onClick={() => handleClearSearch()}>
								<CloseIcon fontSize="medium" />
							</IconButton>
						</Box>
					)}
				</Box>
			</Box>
			{filteredList.length === 0 ? (
				<Box className={classes.vhWidgetBodyNoResults}>
					<p className={classes.noResultsTitle}>{VISUAL_HIERARCHY_NO_RESULTS_TITLE}</p>
					<p className={classes.noResultsDescription}>{VISUAL_HIERARCHY_NO_RESULTS_DESCRIPTION}</p>
				</Box>
			) : (
				<Box className={classes.vhWidgetBody}>
					{widgetLeftPanel()}
					<Divider orientation="vertical" />
					{widgetRightPanel()}
				</Box>
			)}
			<Divider />

			{/* Footer */}
			<Box className={classes.vhWidgetFooter}>
				<Box className={classes.vhWidgetInfo}>{`${VH_MAX_SELECT_MESSAGE.replace(
					'{field}',
					filterName
				)} (${numberOfOptionsSelected}/30)`}</Box>
				<Divider />
				{/* Action Buttons */}
				<Box className={classes.vhWidgetAction}>
					<Box>
						<Button
							id="visual-hierarchy-widget-clear-all"
							disableTouchRipple
							disabled={isButtonDisabled(numberOfOptionsSelected, CLEAR_ALL)}
							onClick={() => onClearAll()}
							className={
								isButtonDisabled(numberOfOptionsSelected, CLEAR_ALL)
									? classes.clearAllDisabledBtn
									: classes.clearAllBtn
							}>
							{CLEAR_ALL}
						</Button>
					</Box>
					<Box className={classes.vhActionRight}>
						<Button
							id="visual-hierarchy-widget-cancel"
							onClick={onCancel}
							disableTouchRipple
							className={`${classes.vhActionBtn} ${classes.vhSecondaryBtn}`}>
							{CANCEL}
						</Button>
						<Button
							id="visual-hierarchy-widget-okay"
							disableTouchRipple
							disabled={isButtonDisabled(numberOfOptionsSelected, OKAY)}
							onClick={onOkay}
							className={
								isButtonDisabled(numberOfOptionsSelected, OKAY)
									? `${classes.vhActionBtn}  ${classes.vhDisabledActionBtn}`
									: `${classes.vhActionBtn} ${classes.vhPrimaryBtn}`
							}>
							{OKAY}
						</Button>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default VisualHierarchyWidget;
